import {
  CoverageCalculator,
  CoverageCalculatorOptions,
  CustomerInfo,
  ItemInfo,
} from ".";
import {
  getFinalSaleReturnsPrice,
  getPricingRulesInLocalCurrency,
  listsHaveMatchingElement,
} from "./calculator-utils";
import { itemValidForFinalSaleReturns } from "./check-product";

const isProductEnabled = (team: CoverageCalculatorOptions) => {
  return !!team.settings.returns?.finalSaleReturns?.enabled;
};

const isShopperEligible = (
  customerInfo: CustomerInfo,
  team: CoverageCalculatorOptions,
) => {
  if (
    listsHaveMatchingElement(
      customerInfo.customerTags,
      team.settings.coverageExcludeCustomerTags,
    )
  ) {
    return false;
  }

  const finalSaleReturnsEligibleCountries =
    getAllFinalSaleReturnsEligibleCountries(team);

  const ValidCountry =
    finalSaleReturnsEligibleCountries.includes(customerInfo.country) ||
    finalSaleReturnsEligibleCountries.length === 0;

  if (!ValidCountry) {
    return false;
  }

  if (
    customerInfo.isBusinessAccount &&
    team.settings.shopifyExtension?.disableBusinessAccountInStorefront
  ) {
    return false;
  }

  if (customerInfo.usingZapietPickup) {
    return false;
  }

  return true;
};

const isItemEligible = (item: ItemInfo, team: CoverageCalculatorOptions) => {
  if (item.vendor === "re:do") {
    return false;
  }

  return itemValidForFinalSaleReturns({
    itemInfo: { productTags: item.productTags, collections: item.collections },
    validCollections:
      team.settings.returns?.finalSaleReturns?.validCollectionTitles ?? [],
    validProductTags:
      team.settings.returns?.finalSaleReturns?.validProductTags ?? [],
    allEligible:
      team.settings.returns?.finalSaleReturns?.allEligible ?? undefined,
  });
};

// returns a list of countries. Empty list means all countries
function getAllFinalSaleReturnsEligibleCountries(
  team: CoverageCalculatorOptions,
) {
  if (team.settings.returns?.finalSaleReturns?.pricingRuleSet?.length) {
    // The second set of pricing is international. so all countries are eligible.
    if (
      team.settings.returns?.finalSaleReturns?.pricingRuleSet[1] &&
      !team.settings.returns?.finalSaleReturns?.pricingRuleSet[1].countries
        .length
    ) {
      return [];
    } else {
      return team.settings.returns?.finalSaleReturns?.pricingRuleSet[0]
        .countries;
    }
  }
  return [];
}

const calculateCoveragePrice = (
  team: CoverageCalculatorOptions,
  country: string | undefined,
  numProducts: number,
  amount: number,
  exchangeRate?: number,
  hasSubscriptionItem?: boolean,
): number => {
  if (exchangeRate && team.settings.returns?.finalSaleReturns) {
    team.settings.returns.finalSaleReturns.minPrice *= exchangeRate;
    team.settings.returns.finalSaleReturns.maxPrice *= exchangeRate;

    if (team.settings.returns.finalSaleReturns.shippingLabelCoveragePrice) {
      team.settings.returns.finalSaleReturns.shippingLabelCoveragePrice *=
        exchangeRate;
    }

    if (team.settings.returns.finalSaleReturns.pricingRuleSet) {
      team.settings.returns.finalSaleReturns.pricingRuleSet =
        getPricingRulesInLocalCurrency(
          team.settings.returns.finalSaleReturns.pricingRuleSet,
          exchangeRate,
        );
    }
  }

  let coveragePrice = 0;
  if (team.settings.returns?.finalSaleReturns) {
    coveragePrice = getFinalSaleReturnsPrice({
      cartTotal: amount,
      pricingRuleSets: team.settings.returns.finalSaleReturns.pricingRuleSet,
      shippingLabelCoveragePrice:
        team.settings.returns.finalSaleReturns.shippingLabelCoveragePrice,
      country,
      minPrice: team.settings.returns.finalSaleReturns.minPrice,
      maxPrice: team.settings.returns.finalSaleReturns.maxPrice,
      backupPercentage: team.settings.returns.finalSaleReturns.percentage,
    });
  }

  return coveragePrice;
};

// returns a price if coverage is valid. otherwise returns undefined
const getCoverage = (
  team: CoverageCalculatorOptions,
  customerInfo: CustomerInfo,
  items: ItemInfo[],
  conversionRate?: number,
  ignoreMerchantPaidForEnablementCheck?: boolean,
): { eligible: boolean; price?: number } => {
  if (!isProductEnabled(team)) {
    return { eligible: false, price: undefined };
  }
  if (!isShopperEligible(customerInfo, team)) {
    return { eligible: false, price: undefined };
  }

  let totalPrice = 0;
  let totalValidPrice = 0;
  let numValidItems = 0;
  let numInvalidItems = 0;

  for (const item of items) {
    const itemPrice = item.price * item.quantity;
    totalPrice += itemPrice;
    const itemValid = isItemEligible(item, team);
    if (itemValid) {
      totalValidPrice += itemPrice;
      numValidItems += item.quantity;
    } else {
      numInvalidItems += item.quantity;
    }
  }

  if (!isCartEligible(numValidItems, numInvalidItems, totalPrice, team)) {
    return { eligible: false, price: undefined };
  }

  const price = calculateCoveragePrice(
    team,
    customerInfo.country,
    numValidItems,
    totalValidPrice,
    conversionRate,
    items.some((item) => item.hasSubscription),
  );

  return { eligible: true, price };
};

const isCartEligible = (
  numValidItems: number,
  numInvalidItems: number,
  cartTotal: number,
  team: CoverageCalculatorOptions,
) => {
  return numValidItems > 0;
};

export const finalSaleReturnsCoverageCalculator: CoverageCalculator = {
  type: "Final Sale",
  isProductEnabled,
  isShopperEligible,
  isItemEligible,
  isCartEligible,
  // need to handle non usd here.
  calculateCoveragePrice,
  getCoverage,
};
